import { ThemeProvider } from '@mui/material/styles';
import React, { Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { EventBusProvider } from 'utils/EventBus';

import { ProtectedRoutes } from 'hoc/ProtectedRoutes';

import { ROUTES } from 'core/constants/routes';
import { getEnvVarAsBoolean } from 'core/helpers/env-helper';
import { PaymentStatus } from 'core/enums/common';

import NewDashboard from 'pages/Dashboard/NewDashboard';
import Home from 'pages/Home';
import { Login } from 'pages/Login/Login';
import { ResetPassword } from 'pages/ResetPassword/ResetPassword';
import { Settings } from 'pages/Settings/Settings';
import { Support } from 'pages/Support/Support';
import { SignUp } from 'pages/SignUp/SignUp';

import { PaymentResultPage } from 'components/Payment/PaymentResultPage';
import { UpdateMethodResult } from 'components/Profile/Subscription/UpdatePaymentInformation/UpdateMethodResult';

import './App.scss';
import { theme } from './assets/styles/_theme';

const App: React.FC = () => {
	const { i18n } = useTranslation();

	useEffect(() => {
		document.body.classList.toggle('rtl', i18n.language === 'ar');
	}, [i18n.language]);

	return (
		<ThemeProvider theme={theme}>
			<Suspense fallback={<div>Loading...</div>}>
				<EventBusProvider>
					<Routes>
						<Route path={`${ROUTES.LOGIN}/:email/:token`} element={<Login />} />
						<Route path={ROUTES.LOGIN} element={<Login />} />
						<Route
							path={ROUTES.SIGNUP}
							element={
								getEnvVarAsBoolean('REACT_APP_ENABLE_SIGNUP', true) ? (
									<SignUp />
								) : (
									<Navigate to={ROUTES.LOGIN} replace />
								)
							}
						/>
						<Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
						<Route path={ROUTES.CREATE_NEW_PASSWORD} element={<ResetPassword />} />
						<Route element={<ProtectedRoutes />}>
						<Route path={ROUTES.DASHBOARD} element={<NewDashboard />} />
						<Route path={`${ROUTES.BRD}/:email/:token`} element={<Home />} />
						<Route path={ROUTES.BRD} element={<Home />} />
						<Route path={ROUTES.SETTINGS} element={<Settings />} />
						<Route path={ROUTES.SETTINGS_PROFILE} element={<Settings />} />
						<Route path={ROUTES.SETTINGS_SUBSCRIPTION} element={<Settings />} />
						<Route path={ROUTES.SETTINGS_DOCS} element={<Settings />} />
						<Route path={ROUTES.SETTINGS_MODEL} element={<Settings />} />
						<Route path={ROUTES.SUPPORT} element={<Support />} />
							<Route
								path={ROUTES.PAYMENT_SUCCESS}
								element={<PaymentResultPage status={PaymentStatus.success} />}
							/>
							<Route
								path={ROUTES.PAYMENT_CANCEL}
								element={<PaymentResultPage status={PaymentStatus.cancel} />}
							/>
							<Route
								path={ROUTES.METHOD_SUCCESS}
								element={<UpdateMethodResult status={PaymentStatus.success} />}
							/>
							<Route
								path={ROUTES.METHOD_CANCEL}
								element={<UpdateMethodResult status={PaymentStatus.cancel} />}
							/>
						</Route>
					</Routes>

					<ToastContainer
						style={{
							width: window.innerWidth <= 600 ? '300px' : '400px',
							maxWidth: 'fit-content',
						}}
					/>
				</EventBusProvider>
			</Suspense>
		</ThemeProvider>
	);
};

export default App;
